import { Dispatch, SetStateAction, useCallback, useMemo } from "react";
import { LatLngLiteral } from "leaflet";
import { useInternal } from "uikit";

import { Map } from "../../../../../services";
import { useTypedDispatch, useTypedSelector } from "../../../../../redux/store";
import orderPage from "../../../../../redux/reducers/OrdersPage";

const useGetCoordinatesForSettlement: useGetCoordinatesForSettlement.Props2 =
	() => {
		const dispatch = useTypedDispatch();
		const language = useTypedSelector((state) => state.session.language);
		const { searchRadiusMeters: searchRadius } = useTypedSelector(
			(state) => state.settings.map,
		);

		const [coordinates, setCoordinates] = useInternal<LatLngLiteral | null>(
			null,
		);
		const [response, setResponse] = useInternal<Map.Search.Object | null>(
			null,
		);
		const [settlement, setSettlement] = useInternal<string | null>(null);

		const get = useCallback<useGetCoordinatesForSettlement.Props["get"]>(
			async (data, settlementQuery) => {
				const optionsType: Map.Search.Type[] = [
					"favorite",
					"localObject",
					"object",
				];

				if (data && settlementQuery && data.placeId) {
					setSettlement(settlementQuery);

					const { searchType } = data as any;

					if (
						searchType === "osm_nominatim" &&
						data.coordinates?.lat &&
						data.coordinates?.lng
					) {
						const updatedData: Map.Search.Object = {
							...data,
							settlement: settlementQuery,
						} as Map.Search.Object;
						setResponse(updatedData);
						setCoordinates(data.coordinates);
						return updatedData;
					}

					const searchOptions: Map.Search.Options = {
						language,
						searchType: "google_geocode",
						country: "ua",
						near: {
							point: data.coordinates,
							radius: searchRadius,
						},
					};

					const result = await Map.search(
						String(data.placeId),
						searchOptions,
					);

					if (result.objects?.length) {
						const [settlementData] = result.objects;
						if (settlementData) {
							const updatedData: Map.Search.Object = {
								...data,
								coordinates: settlementData.coordinates,
								id: settlementData.id,
								placeId: settlementData.placeId,
							} as Map.Search.Object;
							setResponse(updatedData);
							setCoordinates(settlementData.coordinates);
							return updatedData;
						}
						setResponse({
							...data,
							settlement: settlementQuery,
						} as Map.Search.Object);
						return {
							...(data as any),
							settlement: settlementQuery,
						} as Map.Search.Object;
					}
					setResponse({
						...data,
						settlement: settlementQuery,
					} as Map.Search.Object);
					return {
						...(data as any),
						settlement: settlementQuery,
					} as Map.Search.Object;
				}

				if (
					data?.type &&
					optionsType.includes(data.type) &&
					!settlementQuery
				) {
					setResponse(data as Map.Search.Object);
					setCoordinates(data.coordinates);
					return data as Map.Search.Object;
				}

				return null;
			},

			[
				language,
				searchRadius,
				setCoordinates,
				setSettlement,
				setResponse,
			],
		);

		useMemo(() => {
			if (coordinates)
				dispatch(orderPage.actions.setMapCenter(coordinates));
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [coordinates]);

		const reset = useCallback(() => {
			setResponse(null);
			setSettlement(null);
		}, [setResponse, setSettlement]);

		return useMemo<useGetCoordinatesForSettlement.Props>(
			() => ({
				settlement,
				response,
				coordinates,
				reset,
				get,
				setSettlement,
				setResponse,
			}),
			[
				settlement,
				response,
				coordinates,
				reset,
				get,
				setSettlement,
				setResponse,
			],
		);
	};

declare namespace useGetCoordinatesForSettlement {
	type Props2 = () => Props;
	interface SearchQuery {
		settlement?: string;
		settlementType?: string;
		region?: string;
		district?: string;
	}

	interface Props {
		response: Map.Search.Object | null;
		setResponse: Dispatch<SetStateAction<Map.Search.Object | null>>;
		coordinates: LatLngLiteral | null;
		get: (
			data: Partial<Map.Search.Object> &
				SearchQuery & {
					coordinates: LatLngLiteral;
					placeId?: string;
				},
			settlementQuery: string,
		) => Promise<Map.Search.Object | null>;
		reset: () => void;
		settlement: string | null;
		setSettlement: Dispatch<SetStateAction<string | null>>;
	}
}

export default useGetCoordinatesForSettlement;
